import React from "react";
import { Controller, useForm } from "react-hook-form";
import { View } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import CustomTextInput from "../../common/general/CustomTextInput/CustomTextInput";
import FormLayout from "../ActivityForms/common/FormLayout";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const MAX_MODULES = 35;
const MAX_ACTIVITIES = 5;
const MIN_MODULES = 1;
const MIN_ACTIVITIES = 1;

const courseGeneratorSettingsSchema = z.object({
  numberOfModules: z.coerce
    .number()
    .min(MIN_MODULES, `Number of modules must be at least ${MIN_MODULES}`)
    .max(MAX_MODULES, `Number of modules cannot exceed ${MAX_MODULES}`),
  numberOfActivities: z.coerce
    .number()
    .min(MIN_ACTIVITIES, `Number of activities per module must be at least ${MIN_ACTIVITIES}`)
    .max(MAX_ACTIVITIES, `Number of activities per module cannot exceed ${MAX_ACTIVITIES}`),
});

type CourseGeneratorSettingsFormData = z.infer<typeof courseGeneratorSettingsSchema>;

interface CourseGeneratorSettingsProps {
  onSubmit: (data: CourseGeneratorSettingsFormData) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

const CourseGeneratorSettings = ({ onSubmit, onCancel, isSubmitting }: CourseGeneratorSettingsProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<CourseGeneratorSettingsFormData>({
    defaultValues: {
      numberOfModules: 1,
      numberOfActivities: 1,
    },
    resolver: zodResolver(courseGeneratorSettingsSchema),
  });

  return (
    <View>
      <FormLayout
        title="Course Generator Settings"
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onSubmit={handleSubmit(onSubmit)}
        actionText="Generate Course"
        error={errors?.numberOfModules?.message || errors?.numberOfActivities?.message}>
        <CustomText text="Number of Modules" size="s" weight="ultraBold" />
        <CustomSpacing type="vertical" size="s" />
        <Controller
          name="numberOfModules"
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <CustomTextInput
              placeholder="Enter number of modules"
              onBlur={onBlur}
              onChangeText={(text) => onChange(text)}
              value={value?.toString()}
              style={{ width: "100%" }}
              keyboardType="numeric"
              inputMode="numeric"
              error={errors.numberOfModules?.message}
            />
          )}
        />
        <CustomSpacing type="vertical" size="xl" />
        <CustomText text="Number of Activities per Module" size="s" weight="ultraBold" />
        <CustomSpacing type="vertical" size="s" />
        <Controller
          name="numberOfActivities"
          control={control}
          render={({ field: { onChange, onBlur, value } }) => (
            <CustomTextInput
              placeholder="Enter number of activities per module"
              onBlur={onBlur}
              onChangeText={(text) => onChange(text)}
              value={value?.toString()}
              style={{ width: "100%" }}
              keyboardType="numeric"
              inputMode="numeric"
              error={errors.numberOfActivities?.message}
            />
          )}
        />
      </FormLayout>
    </View>
  );
};

export default CourseGeneratorSettings;
